import { RecalculateDryingsStatus } from "@/data/models/ClientAdministrationRow";

/**
 * The Hilfsfunktionen card on a client's detail in the IRES Admin Client (#2610077): the one action is
 * "Alle Trocknungen neu berechnen", which starts a fresh ASSIST recalculation of every drying of the
 * client. Pure logic for its feedback; IRES Cloud does the work and records the run in the Verlauf.
 */

type Translate = (key: string, values?: unknown[]) => string;

const PREFIX = "admin.clients.helperFunctions.recalculateAllDryings";

/** A snackbar to show: whether it is a success (green) or an error (red), and its i18n key with params. */
export interface HelperFunctionSnackbar {
  success: boolean;
  key: string;
  params?: string[];
}

/**
 * The snackbar for a recalculation result: `started` and `already_running` are non-errors (a job is or
 * will be running), `no_op` is shown as an error because nothing happened. Reuses the texts the old IRES
 * Hilfsfunktionen page used.
 */
export function recalculateDryingsResultSnackbar(status: RecalculateDryingsStatus): HelperFunctionSnackbar {
  switch (status) {
    case "started":
      return { success: true, key: `${PREFIX}.started` };
    case "already_running":
      return { success: true, key: `${PREFIX}.alreadyRunning` };
    case "no_op":
      return { success: false, key: `${PREFIX}.noOp` };
  }
}

/** The per-reason refusal keys IRES Cloud can send in `details.reason` (not `ASSIST_FAILED`, handled apart). */
const REFUSAL_KEYS: Record<string, string> = {
  INVALID: `${PREFIX}.refusals.INVALID`,
  NO_CLIENT: `${PREFIX}.refusals.NO_CLIENT`,
  DELETED_CLIENT: `${PREFIX}.refusals.DELETED_CLIENT`,
  SHARED_LOGIN: `${PREFIX}.refusals.SHARED_LOGIN`,
};

/**
 * The error snackbar for a failed recalculation, by IRES Cloud's refusal: `ASSIST_FAILED` carries the
 * backend message, the named reasons their own text, a `permission-denied` the missing-right text, and
 * anything else the general failure.
 */
export function recalculateDryingsErrorSnackbar(error: unknown): HelperFunctionSnackbar {
  const err = error as { code?: unknown; details?: { reason?: unknown; apiMessage?: unknown } } | null;
  const reason = err?.details?.reason;

  if (reason === "ASSIST_FAILED") {
    const apiMessage = typeof err?.details?.apiMessage === "string" ? err.details.apiMessage : "";
    return { success: false, key: `${PREFIX}.error`, params: [apiMessage] };
  }
  if (typeof reason === "string" && REFUSAL_KEYS[reason]) {
    return { success: false, key: REFUSAL_KEYS[reason] };
  }
  if (err?.code === "permission-denied" || err?.code === "functions/permission-denied") {
    return { success: false, key: `${PREFIX}.refusals.PERMISSION_DENIED` };
  }
  return { success: false, key: `${PREFIX}.failed` };
}

/** The status texts of the Verlauf (Gestartet / Läuft bereits / Nichts zu tun); an unknown one as stored. */
const STATUS_TEXT_KEYS: Record<string, string> = {
  started: `${PREFIX}.statusStarted`,
  "already_running": `${PREFIX}.statusAlreadyRunning`,
  "no_op": `${PREFIX}.statusNoOp`,
};

/** A recalculation run's status, as the Verlauf shows it. */
export function recalculateDryingsStatusText(status: string, t: Translate): string {
  return STATUS_TEXT_KEYS[status] ? t(STATUS_TEXT_KEYS[status]) : status;
}
